import { useRef, useState } from 'react';
import { VsmDiagram, type VsmDiagramHandle } from './components/VsmDiagram';
import { AnswerPanel } from './components/AnswerPanel';
import { useSnapshot } from './hooks/useSnapshot';
import { postQuery, type QueryResponse } from './api';

export function App() {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<QueryResponse | null>(null);
  const [queryError, setQueryError] = useState<string | null>(null);
  const diagramRef = useRef<VsmDiagramHandle>(null);
  const { snapshot, error: snapshotError } = useSnapshot();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const q = text.trim();
    if (!q) return;
    setBusy(true);
    setQueryError(null);
    try {
      const r = await postQuery(q);
      setResult(r);
      diagramRef.current?.highlightRegions(r.regions);
      if (r.include_algedonic_pulse) diagramRef.current?.triggerAlgedonicPulse();
    } catch (err) {
      setQueryError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className='layout'>
      <header>
        <h1>VASM — Viable System Query</h1>
        <form className='query-form' onSubmit={submit}>
          <input
            type='text'
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder='Ask the substrate — e.g. what is S3★ doing right now?'
            disabled={busy}
          />
          <button type='submit' disabled={busy || !text.trim()}>
            {busy ? '…' : 'Ask'}
          </button>
        </form>
      </header>
      <main>
        <div className='diagram-pane'>
          <VsmDiagram ref={diagramRef} snapshot={snapshot} />
        </div>
        <aside>
          <AnswerPanel
            result={result}
            queryError={queryError}
            snapshot={snapshot}
            snapshotError={snapshotError}
          />
        </aside>
      </main>
    </div>
  );
}
